import {useAccessToken, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {uploadBulkDispute, Dispute, findDisputes, StatusType, StatusTypeValue} from "../../resources/dispute"
import React, {useEffect, useMemo, useState} from "react"
import {Filter} from "../Filter/Filter"
import {useQueryState} from "use-location-state"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {ErrorDocument, Meta, Resource} from "../../resources/common"
import SearchBox from "../SearchBox/SearchBox"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faCircleNotch, faUpload} from "@fortawesome/free-solid-svg-icons"
import {useToasts} from "react-toast-notifications"
import {startCase} from "lodash"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import classNames from "classnames"
import {UnitAdminOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {NewDisputeModal} from "./NewDisputeModal"
import {useModal} from "react-modal-hook"
import {Transaction} from "../../resources/transaction"
import {hasPermission} from "../../services/permission"
import {downloadFile} from "../../utilities/file"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AllowedDisputesColumns} from "./Disputes.types"
import DisputesTable from "./DisputesTable"
import {HasPermission} from "../../containers/PermissionedUser/Permission"

interface DisputesProps {
	token?: string
	limit?: number
	includedColumns: Array<AllowedDisputesColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	enableUpload?: boolean
	customerId?: string
	transaction?: Transaction
	enableSearch?: boolean
	enableOrgFilter?: boolean
	enableStatusFilter?: boolean
	enablePagination?: boolean
	singleDisputeView?: boolean
	enableExport?: boolean
}

function CreateDispute({transaction, refresh}: {transaction: Transaction; refresh: () => void}) {
	const [showDisputeModal, hideDisputeModal] = useModal(() => {
		return (
			<NewDisputeModal
				close={hideDisputeModal}
				refresh={refresh}
				accountId={transaction.relationships.account.data.id}
				transaction={transaction}
			/>
		)
	})

	if (hasPermission("dispute", "create")) {
		return (
			<div className="pagination-row">
				<div className="navigation-items">
					<button className="button is-primary" onClick={() => showDisputeModal()}>
						Create
					</button>
				</div>
			</div>
		)
	}
	return <> </>
}

export function Disputes({
	enableTitle,
	fullHeight,
	includedColumns,
	enableUpload = false,
	customerId,
	transaction,
	enableOrgFilter = false,
	enableSearch = true,
	enableStatusFilter = true,
	enablePagination = true,
	singleDisputeView = false,
	limit = 25,
	enableExport = true,
}: DisputesProps) {
	const pageTitle = singleDisputeView ? "Dispute" : "Disputes"
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const userType = useUserType()
	const {addToast} = useToasts()
	const [isFileLoading, setIsFileLoading] = useState(false)
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>("filter[orgs]", [])
	const [query, setQuery] = useQueryState("filter[query]", "")
	const [filteredDisputeStatuses, setFilteredDisputeStatuses] = useQueryState<string[]>(
		"filter[status]",
		Object.keys(StatusType)
	)
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findDisputes(
				accessToken,
				offset,
				"json",
				limit,
				customerId,
				filteredDisputeStatuses,
				transaction?.id,
				filteredOrgs,
				query,
				"customer,org,account"
			),
		(x) => x.data.length,
		[filteredDisputeStatuses.join(","), refreshToken, filteredOrgs.join(","), query],
		"",
		true
	)
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Disputes", 500, onSearch)
	const [bulkDisputeState, bulkDispute] = useAsyncResultIdle(uploadBulkDispute)

	const statusFilter = (
		<Filter
			title="Status"
			setStatuses={setFilteredDisputeStatuses}
			statuses={filteredDisputeStatuses}
			onFilterFunc={() => reset(limit)}
			options={new Map(Object.keys(StatusType).map((t) => [t, StatusTypeValue(t)]))}
		/>
	)

	const orgs =
		(userType === "unit" || userType === "bank") && enableOrgFilter
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	useEffect(() => {
		bulkDisputeState.match(
			() => null,
			() => setIsFileLoading(true),
			(ok) => {
				addToast(
					`Successfully handled disputes imported file: ${Object.entries(ok.attributes)
						.map((v) => startCase(v[0]) + ": " + v[1])
						.join(", ")}`,
					{appearance: "success"}
				)
				setIsFileLoading(false)
				refresh()
			},
			(err) => {
				const errorTitle = err.errors ? err.errors[0].title : "An unexpected error has occurred"
				const errorDetail = err.errors ? err.errors[0].detail : ""
				addToast(`${errorTitle} - ${errorDetail}`, {appearance: "error"})
				setIsFileLoading(false)
			}
		)
	}, [bulkDisputeState])

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	function onFileUploaded(e: React.ChangeEvent<HTMLInputElement>) {
		const files = e.target.files
		if (files) {
			const file = files[0]
			if (file) {
				setIsFileLoading(true)
				bulkDispute(accessToken, file, "text/csv", file.name)
			}
		}
	}

	async function exportDisputes() {
		const response = await findDisputes(
			accessToken,
			0,
			"csv",
			null,
			customerId,
			filteredDisputeStatuses,
			transaction?.id,
			filteredOrgs,
			query,
			"org"
		)

		response.match(
			(d) => downloadFile("Disputes.csv", d, "text/csv"),
			() => {
				addToast("Something went wrong", {appearance: "warning"})
			}
		)
		return true
	}

	return (
		<DataTableCard className={"disputes-card"}>
			<UnitAdminOnly>
				{enableUpload ? (
					<div className="file column is-right is-flex is-align-items-right">
						<a className="is-flex is-align-items-right ml-1">
							<label className="file-label">
								<input
									className="file-input"
									type="file"
									disabled={isFileLoading}
									onChange={(e) => onFileUploaded(e)}
									accept=".text,.csv"
								/>
								<span className={classNames("file-cta", isFileLoading && "file-input-disabled")}>
									<span className="file-icon">
										<FontAwesomeIcon icon={isFileLoading ? faCircleNotch : faUpload} spin={isFileLoading} />
									</span>
									<span className="file-label">Upload Disputes CSV file</span>
								</span>
							</label>
						</a>
					</div>
				) : null}
			</UnitAdminOnly>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={enableSearch ? searchBox : null}
				filters={[...(enableOrgFilter ? [orgFilter] : []), ...(enableStatusFilter ? [statusFilter] : [])]}
				exportFunc={enableExport ? exportDisputes : undefined}
				title={enableTitle ? pageTitle : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value}) => {
					const disputes = value as {data: Dispute[]; meta: Meta; included: Resource[]}

					if (singleDisputeView && disputes.data.length === 0) {
						return (
							<>
								<HasPermission resource="dispute" action="create">
									<CreateDispute transaction={transaction as Transaction} refresh={refresh} />
								</HasPermission>
							</>
						)
					}
					return (
						<DisputesTable
							disputes={disputes.data}
							meta={disputes.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							refresh={refresh}
							isUsingPaging={enablePagination}
							include={disputes.included}
							token={accessToken}
							fullHeight={fullHeight}
							includedColumns={includedColumns}
							singleDisputeView={singleDisputeView}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
